import React from "react";
import Section from "./Section";

export default function HTML_Tables() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">📊 HTML Tables</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        HTML tables allow you to display structured data using rows and columns.
        The main elements include{" "}
        <code className="highlight_code_1">&lt;table&gt;</code>,
        <code className="highlight_code_1">&lt;tr&gt;</code>,{" "}
        <code className="highlight_code_1">&lt;th&gt;</code>, and{" "}
        <code className="highlight_code_1">&lt;td&gt;</code>.
      </p>

      {/* 🔹 Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#0284c7",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          📌 Common Table Tags
        </h3>

        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code_1">&lt;table&gt;</code> — Creates
            the table
          </li>
          <li>
            <code className="highlight_code_1">&lt;tr&gt;</code> — Table row
          </li>
          <li>
            <code className="highlight_code_1">&lt;th&gt;</code> — Header cell
            (bold & centered)
          </li>
          <li>
            <code className="highlight_code_1">&lt;td&gt;</code> — Standard
            table cell
          </li>
          <li>
            <code className="highlight_code_1">&lt;caption&gt;</code> — Table
            title
          </li>
          <li>
            <code className="highlight_code_1">&lt;thead&gt;</code>,{" "}
            <code className="highlight_code_1">&lt;tbody&gt;</code>,{" "}
            <code className="highlight_code_1">&lt;tfoot&gt;</code> — Table
            sections
          </li>
          <li>
            <code className="highlight_code_1">colspan</code> &{" "}
            <code className="highlight_code_1">rowspan</code> — Merge cells
          </li>
        </ul>
      </div>

      {/* Section 1️⃣ — Basic Table */}
      <Section
        title="1️⃣ Basic HTML Table"
        html={`<table >
  <tr>
    <th>Name</th>
    <th>Age</th>
  </tr>
  <tr>
    <td>John</td>
    <td>25</td>
  </tr>
</table>`}
        description="This is the simplest table containing rows and columns using <table>, <tr>, <th>, and <td>."
      />

      {/* Section 2️⃣ — Table with Caption */}
      <Section
        title="2️⃣ Table With Caption"
        html={`<table border="1">
  <caption>Student Report</caption>
  <tr>
    <th>Name</th>
    <th>Marks</th>
  </tr>
  <tr>
    <td>Aisha</td>
    <td>92</td>
  </tr>
</table>`}
        description="The <caption> tag adds a title above the table."
      />

      {/* Section 3️⃣ — Table Sections */}
      <Section
        title="3️⃣ Table Sections — thead, tbody, tfoot"
        html={`<table border="1">
  <thead>
    <tr>
      <th>Item</th>
      <th>Price</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Pen</td>
      <td>$1</td>
    </tr>
    <tr>
      <td>Notebook</td>
      <td>$3</td>
    </tr>
  </tbody>
  <tfoot>
    <tr>
      <td>Total</td>
      <td>$4</td>
    </tr>
  </tfoot>
</table>`}
        description="Using <thead>, <tbody>, and <tfoot> improves readability and helps accessibility tools interpret the table."
      />

      {/* Section 4️⃣ — Cell Spacing & Padding */}
      <Section
        title="4️⃣ Cell Padding & Spacing"
        html={`<table border="1" cellpadding="10" cellspacing="5">
  <tr>
    <th>Product</th>
    <th>Price</th>
  </tr>
  <tr>
    <td>Keyboard</td>
    <td>$20</td>
  </tr>
</table>`}
        description="Cellpadding adds space inside cells. Cellspacing adds space between cells (not recommended in modern HTML, use CSS instead)."
      />

      {/* Section 5️⃣ — Colspan & Rowspan */}
      <Section
        title="5️⃣ Merging Cells (colspan & rowspan)"
        html={`<table border="1">
  <tr>
    <th colspan="2">Employee Info</th>
  </tr>
  <tr>
    <td>Name</td>
    <td>Rahul</td>
  </tr>
  <tr>
    <td rowspan="2">Role</td>
    <td>Developer</td>
  </tr>
  <tr>
    <td>Designer</td>
  </tr>
</table>`}
        description="colspan merges columns, and rowspan merges rows."
      />

      {/* Section 6️⃣ — Table Border & Styling */}
      <Section
        title="6️⃣ Table Styling Using CSS"
        html={`<table style="width:100%; border-collapse: collapse;">
  <tr style="background:#e0f2fe;">
    <th style="border:1px solid #000; padding:8px;">Name</th>
    <th style="border:1px solid #000; padding:8px;">Email</th>
  </tr>
  <tr>
    <td style="border:1px solid #000; padding:8px;">Sam</td>
    <td style="border:1px solid #000; padding:8px;">sam@example.com</td>
  </tr>
</table>`}
        description="Using CSS you can control table layout, padding, colors, borders, and spacing."
      />

      {/* Section 7️⃣ — Responsive Table */}
      <Section
        title="7️⃣ Responsive Table (Scrollable)"
        html={`<div style="overflow-x:auto;">
  <table border="1">
    <tr>
      <th>Country</th>
      <th>Capital</th>
      <th>Population</th>
      <th>Currency</th>
      <th>Region</th>
    </tr>
    <tr>
      <td>India</td>
      <td>New Delhi</td>
      <td>1.4B</td>
      <td>INR</td>
      <td>Asia</td>
    </tr>
  </table>
</div>`}
        description="Wrap tables inside a container with overflow-x:auto to make them scroll on smaller screens."
      />

      {/* Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Tables</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>&lt;table&gt;</b> contains table data
          </li>
          <li>
            <b>&lt;tr&gt;</b> defines rows
          </li>
          <li>
            <b>&lt;th&gt;</b> is for header cells
          </li>
          <li>
            <b>&lt;td&gt;</b> is for regular cells
          </li>
          <li>
            Use <b>colspan</b> and <b>rowspan</b> to merge cells
          </li>
          <li>
            <b>CSS</b> is recommended for spacing and styling
          </li>
          <li>
            Tables can be made <b>responsive</b> using scrollable containers
          </li>
        </ul>
      </div>
    </div>
  );
}
